import { supertypeClass, amorphicStatic, Supertype } from '@haventech/amorphic';

import { Account, OnlineAccountProvider, OnlineAccountAccess, OnlineAccountStatuses } from '../Account';
import { AccountAuthorization } from '../authorization/AccountAuthorization';
import { Auth0Client } from '../../authentication/Auth0Client';
import { Identity, IdentityProvidersDisplayName, IdentitySource, FullIdentity } from './Identity';
import { ManagedEntity } from '../../customer/ManagedEntity';
import { ManagedEntityType } from '../authorization/ManagedEntityType';
import { Customer } from '../../customer/Customer';
import { Auth0Utils, Auth0Channel } from '../../authentication/Auth0Utils';
import { UILoggerSingleton, UICategory } from '../../logger/UILoggerSingleton';

const IDENTITY_SEPARATOR = '|';

@supertypeClass({ toClient: false, toServer: false })
export class IdentityService extends Supertype {
  private static serviceInstance: IdentityService;

  private constructor() {
    super();
  }

  private init() {
    return this;
  }

  public static getService(): IdentityService {
    if (!IdentityService.serviceInstance) {
      IdentityService.serviceInstance = new IdentityService().init();
    }

    return IdentityService.serviceInstance;
  }

  public getProviderFromIdentifier(identifier: string): string {
    const identifierParts = identifier.split(IDENTITY_SEPARATOR);
    const type = identifierParts[0];

    return type;
  }

  public getIdFromIdentifier(identifier: string): string {
    const identifierParts = identifier.split(IDENTITY_SEPARATOR);
    const id = identifierParts[1];

    return id;
  }

  public getAccountAuthByTemplateFromAccount(customerAccount: Account, amorphicClassName: string): any {
    if (!customerAccount) {
      return;
    } else {
      return customerAccount.accountAuthorizations.find((accountAuth) => {
        return accountAuth.managedEntityType.templateName === amorphicClassName;
      });
    }
  }

  public async createNewIdentity(identifier: string, isPrimary: boolean, source?: IdentitySource): Promise<Identity> {
    let identity: Identity;

    if (identifier) {
      identity = await this.loadIdentityByIdentifier(identifier);

      if (!identity) {
        identity = Identity.new(identifier);
        identity.source = source ? source : IdentitySource.SIGN_UP;

        await this.setIsPrimary(identity, isPrimary);
        await this.unlockIdentity(identity);

        const provider = this.getProviderFromIdentifier(identifier);
        identity.provider = provider;

        await identity.persistSave();

        UILoggerSingleton.getInstance().info(
          {
            module: 'IdentityService',
            function: 'createNewIdentity',
            category: UICategory.milestone,
            data: {
              identifier: identifier,
              provider: provider,
            },
          },
          'Created new identity'
        );
      }
    }

    return identity;
  }

  public async createNewIdentityForAccount(
    identifier: string,
    account: Account,
    isPrimary: boolean,
    source?: IdentitySource
  ): Promise<Identity> {
    let identity: Identity;

    identity = await this.createNewIdentity(identifier, isPrimary, source);

    return this.linkIdentityToAccount(identity, account);
  }

  public async loadIdentityByIdentifier(identifier: string): Promise<Identity> {
    let identity: Identity = null;

    if (identifier) {
      const fetchedIdentities: Array<Identity> = await Identity.persistorFetchByQuery({
        identifier: identifier,
        isActive: 'true',
      });

      if (fetchedIdentities && fetchedIdentities.length > 0) {
        if (fetchedIdentities.length > 1) {
          UILoggerSingleton.getInstance().error(
            {
              module: 'IdentityService',
              function: 'loadIdentityByIdentifier',
              category: UICategory.request,
              error: {
                isHumanRelated: false,
                data: {
                  identifier: identifier,
                },
              },
            },
            'Multiple identities found for identifier'
          );
        }

        identity = fetchedIdentities[0];
      }
    }

    return identity;
  }

  public async associateApplicationWithIdentity(
    managedEntity: ManagedEntity,
    identity: Identity
  ): Promise<AccountAuthorization> {
    let customerAccount: Account = null;

    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'associateApplicationWithIdentity',
        category: UICategory.request,
      },
      'Starting to associate app with identity'
    );

    if (identity.account) {
      customerAccount = customerAccount || identity.account;

      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'associateApplicationWithIdentity',
          category: UICategory.request,
        },
        'Customer account exists lucky us'
      );
    } else {
      // Create one if one doesn't exist
      if (!customerAccount) {
        UILoggerSingleton.getInstance().info(
          {
            module: 'IdentityService',
            function: 'associateApplicationWithIdentity',
            category: UICategory.request,
          },
          'Customer account does not exist, time to create one'
        );

        customerAccount = Account.new(identity);
        await customerAccount.persistSave();

        UILoggerSingleton.getInstance().info(
          {
            module: 'IdentityService',
            function: 'associateApplicationWithIdentity',
            category: UICategory.milestone,

            data: {
              identityId: identity._id,
              managedEntityId: managedEntity._id,
              managedEntityType: managedEntity.amorphicGetClassName(),
              customerAccountId: customerAccount._id,
            },
          },
          'Created new Account'
        );
      }

      await this.linkIdentityToAccount(identity, customerAccount);
    }

    const accountAuthorization: AccountAuthorization = await this.loadOrCreateAccountAuthorization(
      customerAccount,
      managedEntity
    );

    // Update session:
    identity.account = customerAccount;

    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'associateApplicationWithIdentity',
        category: UICategory.request,
        data: {
          identityId: identity._id,
          managedEntityId: managedEntity._id,
          managedEntityType: managedEntity.amorphicGetClassName(),
        },
      },
      'Associated managed entity with identity'
    );

    return accountAuthorization;
  }

  public async linkIdentityToAccount(identity: Identity, account: Account): Promise<Identity> {
    identity.account = account;
    await identity.persistSave();

    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'linkIdentityToAccount',
        category: UICategory.request,
        data: {
          identityId: identity._id,
          account: account._id,
        },
      },
      'Linked identity to account'
    );

    account.identities.push(identity);

    return identity;
  }

  public async loadOrCreateAccountAuthorization(
    account: Account,
    managedEntity: ManagedEntity
  ): Promise<AccountAuthorization> {
    let accountAuthorization: AccountAuthorization;

    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'loadOrCreateAccountAuthorization',
        UICategory: UICategory.request,
      },
      'Starting to load or create account authorization'
    );

    // If authorization already exists, find it
    const existingAuthorizedAccounts: Array<AccountAuthorization> = await this.loadAuthorizedAccountsForManagedEntity(
      managedEntity
    );
    if (existingAuthorizedAccounts) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'loadOrCreateAccountAuthorization',
          UICategory: UICategory.request,
        },
        'Account Authorization already exists for this account'
      );

      accountAuthorization = existingAuthorizedAccounts.find((authorizedAccount) => {
        if (authorizedAccount.account) {
          return authorizedAccount.account._id === account._id;
        }

        return false;
      });
    }

    // Create Authorized Account if it doesn't already exist
    if (!accountAuthorization) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'loadOrCreateAccountAuthorization',
          UICategory: UICategory.request,
        },
        'Account Authorization does not exists for this account, will create one'
      );

      const managedEntityType: ManagedEntityType = await this.loadOrCreateManagedEntityTypeByName(
        managedEntity.amorphicGetClassName()
      );
      accountAuthorization = AccountAuthorization.new(account, managedEntityType, managedEntity._id);
      await accountAuthorization.persistSave();

      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'loadOrCreateAccountAuthorization',
          UICategory: UICategory.request,

          data: {
            managedEntityId: managedEntity._id,
            managedEntityType: managedEntityType.templateName,
            accountId: account._id,
          },
        },
        'Created new account authorization'
      );

      account.accountAuthorizations.push(accountAuthorization);
    }

    return accountAuthorization;
  }

  public async loadOrCreateManagedEntityTypeByName(templateName: string): Promise<ManagedEntityType> {
    let type: ManagedEntityType;

    // If authorization type already exists, find it
    const types: Array<ManagedEntityType> = await ManagedEntityType.getFromPersistWithQuery({
      templateName: templateName,
    });

    if (types && types.length > 0) {
      type = types[0];
    } else {
      type = ManagedEntityType.new(templateName);
      await type.persistSave();
    }

    return type;
  }

  public async loadAuthorizedAccountsForManagedEntity(
    managedEntity: ManagedEntity
  ): Promise<Array<AccountAuthorization>> {
    const managedEntityType: ManagedEntityType = await this.loadOrCreateManagedEntityTypeByName(
      managedEntity.amorphicGetClassName()
    );

    const accounts: Array<AccountAuthorization> = await AccountAuthorization.persistorFetchByQuery({
      managed_entity_type_id: managedEntityType._id,
      managedEntityId: managedEntity._id,
    });

    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'loadAuthorizedAccountsForManagedEntity',
        UICategory: UICategory.request,

        data: {
          managedEntityId: managedEntity._id,
          managedEntityType: managedEntity.amorphicGetClassName(),
          numberOfManagingAccounts: accounts ? accounts.length : 0,
        },
      },
      'Loading accounts managing identity'
    );

    return accounts;
  }

  /**
   * Invalidates all connections to a managed entity.  This will invalidate ALL identities tied to this managed entity
   * as well as their customer accounts.
   *
   * Note 1: This does not cascade to other managed entities.  That work needs to be done when other managed entities
   * are possible.
   *
   * Note 2: This will invalidate all customer accounts based on this manged identity.  If multiple users manage a customer account,
   *   this needs to be modified
   *
   * @param managedIdentity
   */
  public async invalidateIdentities(
    managedIdentity: ManagedEntity,
    txn,
    channel?: string,
    dontDelete?: boolean
  ): Promise<void> {
    const authorizedAccounts: Array<AccountAuthorization> = await this.loadAuthorizedAccountsForManagedEntity(
      managedIdentity
    );

    for (let i = 0; i < authorizedAccounts.length; i++) {
      await this.invalidateCustomerAccount(authorizedAccounts[i].account, txn, channel, dontDelete);
      await authorizedAccounts[i].persistSave(txn);
    }
  }

  /**
   * Invalidate a user account.  This invalidates identities associated with the account specified.
   * @param customerAccount
   * @param txn
   */
  private async invalidateCustomerAccount(
    customerAccount: Account,
    txn,
    channel?: string,
    dontDelete?: boolean
  ): Promise<void> {
    customerAccount.isActive = false;

    for (let i = 0; i < customerAccount.identities.length; i++) {
      try {
        // will need to make this accept other channels but for now we cannot delete from AMC auth0

        if (!dontDelete) {
          const auth0Channel = Auth0Utils.mapCustomerChannelToAuth0Channel(channel);

          if (auth0Channel === Auth0Channel.HAVEN) {
            await Auth0Client.deleteAuth0User(customerAccount.identities[i].identifier, auth0Channel);
          }
        }

        customerAccount.identities[i].isActive = false;
        await customerAccount.identities[i].persistSave(txn);

        await customerAccount.persistSave(txn);
      } catch (error) {
        UILoggerSingleton.getInstance().error(
          {
            module: 'IdentityService',
            function: 'invalidateCustomerAccount',
            UICategory: UICategory.request,

            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
              data: {
                customerAccount: customerAccount._id,
                identityId: customerAccount.identities[i]._id,
              },
            },
          },
          'There was an issue deleting identity in Auth0'
        );
      }

      UILoggerSingleton.getInstance().warn(
        {
          module: 'IdentityService',
          function: 'invalidateCustomerAccount',
          UICategory: UICategory.request,
        },
        'Invalidating customer identity'
      );
    }
  }

  /**
   * Get the primary identity for a customer, primary being the identity they signed up with.
   * In case the primary identity is social, and later the user wants to unlink that, they must create a
   * regular email/password identity that will be set to SIGN_UP
   * @param customer
   */
  public async getPrimaryIdentity(customer: ManagedEntity): Promise<Identity> {
    const existingAuthorizedAccounts: Array<AccountAuthorization> = await this.loadAuthorizedAccountsForManagedEntity(
      customer
    );
    let primaryIdentity: Identity;

    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'getPrimaryIdentity',
        UICategory: UICategory.request,
      },
      'Getting primary identity for this customer'
    );

    if (existingAuthorizedAccounts) {
      let accountAuthorization: AccountAuthorization;
      accountAuthorization = existingAuthorizedAccounts[0]; // one customer

      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'getPrimaryIdentity',
          UICategory: UICategory.request,

          data: {
            accountAuthorization: accountAuthorization._id,
          },
        },
        'Got authorized account for customer ' + customer._id
      );

      primaryIdentity = Auth0Utils.getPrimaryIdentityFromAccount(accountAuthorization.account);

      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'getPrimaryIdentity',
          UICategory: UICategory.request,
        },
        'Got primary identity for customer'
      );
    }

    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'getPrimaryIdentity',
        UICategory: UICategory.request,
      },
      'Finished getting primary identity'
    );

    return primaryIdentity;
  }
  /**
   * Get the account for a customer.
   * In case the primary identity is social, and later the user wants to unlink that, they must create a
   * regular email/password identity that will be set to SIGN_UP
   * @param customer
   */
  public async getCustomerAccount(customer: Customer): Promise<Account> {
    const existingAuthorizedAccounts: Array<AccountAuthorization> = await this.loadAuthorizedAccountsForManagedEntity(
      customer
    );
    let customerAccount: Account;

    if (existingAuthorizedAccounts.length > 0) {
      //check customer for existence of account
      if (existingAuthorizedAccounts[0].account) {
        customerAccount = existingAuthorizedAccounts[0].account;
      }

      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'getCustomerAccount',
          UICategory: UICategory.request,
        },
        'Retrieved customer account'
      );
    }

    return customerAccount;
  }
  /**
   * Invalidate a user identity.  This invalidates the specified identity of a user when we are unlinking the identity.
   * Unlinks the identity depending on isPrimary
   * Deletes the user from auth0
   * Finally sets the identity to isActive=false
   * @param identifier
   * @param customerAccount
   * @param txn
   */
  public async invalidateIdentity(identityId: string, customerAccount: Account, txn): Promise<void> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'invalidateIdentity',
        UICategory: UICategory.request,

        data: {
          identityId: identityId,
        },
      },
      'Invaldate identity flow has been triggered'
    );

    try {
      const identityToInvalidate = customerAccount.activeIdentities.find((identity) => identity._id === identityId);

      if (!identityToInvalidate) {
        throw Error('Uh oh this identity does not exist in customerAccount');
      }

      await this.preDeleteAuth0user(customerAccount, identityToInvalidate, txn);

      await Auth0Client.deleteAuth0User(identityToInvalidate.identifier, Auth0Channel.HAVEN);

      await this.postDeletAuth0user(identityToInvalidate, txn);

      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'invalidateIdentity',
          UICategory: UICategory.request,

          data: {
            customerAccount: customerAccount._id,
            identityId: identityToInvalidate._id,
          },
        },
        'Identity should be completely invalidated now'
      );
    } catch (error) {
      // yikes
      UILoggerSingleton.getInstance().error(
        {
          module: 'IdentityService',
          function: 'invalidateIdentity',
          UICategory: UICategory.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            data: {
              customerAccount: customerAccount._id,
              identityId: identityId,
            },
          },
        },
        'There was an issue invalidating identity for account'
      );
    }
  }

  /**
   * Handle flow to unlink auth0 user before deleting it from auth0 user base
   * Primary depends on what identity is being invalidated
   * @param customerAccount
   * @param identityToInvalidate
   * @param txn
   */
  public async preDeleteAuth0user(customerAccount: Account, identityToInvalidate: Identity, txn): Promise<void> {
    const primaryIdentity = Auth0Utils.getPrimaryIdentityFromAccount(customerAccount);

    const hasMultipleIdentities = customerAccount.activeIdentities.length > 1;
    const isInvalidatingPrimaryIdentity = identityToInvalidate.isPrimary;

    if (isInvalidatingPrimaryIdentity && hasMultipleIdentities) {
      // delinking primary account, has more than one active identity
      const newPrimaryIdentity: Identity = customerAccount.activeIdentities[1];

      await this.setIsPrimary(identityToInvalidate, true);

      // unlink the new primary account from previous primary social account to have two standalone objects
      // so the auth0 object can be deleted
      await Auth0Client.unlinkAuth0User(
        identityToInvalidate.identifier,
        this.getIdFromIdentifier(newPrimaryIdentity.identifier),
        newPrimaryIdentity.provider
      );
    } else if (!isInvalidatingPrimaryIdentity && hasMultipleIdentities) {
      // not delinking primary, has more than one active identity, still need to unlink it in auth0 before we can delete it
      await Auth0Client.unlinkAuth0User(
        primaryIdentity.identifier,
        this.getIdFromIdentifier(identityToInvalidate.identifier),
        identityToInvalidate.provider
      );
    }
  }

  /**
   * Set identity as inactive and not primary if it was primary
   * @param identityToInvalidate
   * @param txn
   */
  public async postDeletAuth0user(identityToInvalidate: Identity, txn): Promise<void> {
    // identity to invalidate was primary
    await this.setIsPrimary(identityToInvalidate, identityToInvalidate.isPrimary);

    // set identity as inactive
    identityToInvalidate.isActive = false;
    await identityToInvalidate.persistSave(txn);
  }

  /**
   * Lock an identity
   * @param identity
   */
  public async lockIdentity(identity: Identity): Promise<Identity> {
    // should this find the identity from an account by identifier instead?
    if (identity) {
      identity.isLocked = true;
      await identity.persistSave();
    }
    return identity;
  }

  /**
   * Unlock an identity
   * @param identity
   */
  public async unlockIdentity(identity: Identity): Promise<Identity> {
    // should this find the identity from an account by identifier instead?
    if (identity) {
      identity.isLocked = false;
      await identity.persistSave();
    }
    return identity;
  }

  // TODO: Auth0 user interface
  /**
   * Get auth0 user data by identity
   * @param identity
   */
  public async getAuth0User(identity: Identity): Promise<any> {
    return Auth0Client.getAccountInfoById(identity.identifier);
  }

  /**
   * Unlock an account by going through all of its identities and unlocking them
   * @param identity
   */
  public async unlockAccount(identity: Identity): Promise<void> {
    identity.isLocked = false;

    identity.account.activeIdentities.forEach(async (thisIdentity) => {
      if (thisIdentity.isLocked) {
        await this.unlockIdentity(thisIdentity);
      }
    });

    await identity.persistSave();
  }

  /**
   * Set identity isPrimary attribute, can pass it in or will toggle it
   * @param identity
   * @param isPrimary
   */
  public async setIsPrimary(identity: Identity, isPrimary: boolean): Promise<void> {
    if (isPrimary !== undefined) {
      identity.isPrimary = isPrimary;
    } else {
      identity.isPrimary = !identity.isPrimary;
    }

    await identity.persistSave();
  }

  /**
   * mark identity as isActive
   * @param identity
   * @param txn
   */
  public async reactivateIdentity(identity: Identity): Promise<void> {
    identity.isActive = true;
    await identity.persistSave();
  }

  async deleteAccountAuthorization(accountAuthorization: AccountAuthorization) {
    const txn = amorphicStatic.beginTransaction();
    accountAuthorization.setAsDeleted(txn);
    try {
      await amorphicStatic.commit({ transaction: txn });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Given a customer, return their online account access
   * @param customer
   */
  public async getOnlineAccountAccessForCustomer(customer: Customer): Promise<OnlineAccountAccess> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'getOnlineAccountAccess',
        UICategory: UICategory.request,

        data: {
          customerId: customer._id,
        },
      },
      'Get online account access info for customer'
    );

    const existingAuthorizedAccounts: AccountAuthorization[] = await this.loadAuthorizedAccountsForManagedEntity(
      customer
    );

    // Case: legacy
    const isLegacyAccount = existingAuthorizedAccounts.length === 0 && customer.passwordHash;
    if (isLegacyAccount) {
      return this.getOAAForLegacyCustomer(customer);
    }

    // Case: not legacy but has not completed auth process
    const isUnauthorized = existingAuthorizedAccounts.length === 0 && !customer.passwordHash;
    if (isUnauthorized) {
      return this.getOAAUnauthorizedCustomer(customer);
    }

    // Case: has account auth, regular flow
    const isAuthorized = existingAuthorizedAccounts.length > 0;
    if (isAuthorized) {
      return this.getOAAForAuthorizedCustomer(customer, existingAuthorizedAccounts[0].account);
    }

    return null;
  }

  /**
   * Given a customer and customer account, return online access account for authorized customer
   * Authorized customers are ones in Auth0 or AMC Auth0
   * @param customer
   * @param customerAccount
   */
  public async getOAAForAuthorizedCustomer(customer: Customer, customerAccount: Account): Promise<OnlineAccountAccess> {
    let accountAuthorization: AccountAuthorization;
    if (customerAccount.accountAuthorizations && customerAccount.accountAuthorizations.length > 0) {
      accountAuthorization = customerAccount.accountAuthorizations[0];
    }
    const oaa: OnlineAccountAccess = {
      status: OnlineAccountStatuses.authorized,
      accountId: customerAccount._id,
      accountCreatedTime: customerAccount.createdTime,
      accountAuthorizationCreatedTime: accountAuthorization && accountAuthorization.createdTime,
      identityProvider:
        Auth0Utils.mapCustomerChannelToAuth0Channel(customer.channel) !== Auth0Channel.HAVEN
          ? OnlineAccountProvider.AMC
          : OnlineAccountProvider.AUTH0,
      identities: [],
    };

    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'getOAAForAuthorizedCustomer',
        UICategory: UICategory.request,

        data: {
          customerId: customer._id,
          accountId: oaa.accountId,
          accountCreatedTime: oaa.accountCreatedTime,
        },
      },
      'Customer has account authorizations'
    );

    const primaryIdentity = customerAccount.identities.find((id) => id.isPrimary);

    if (!primaryIdentity) {
      // uh something went really wrong if a primary identity was not created at this point
      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'getOAAForAuthorizedCustomer',
          UICategory: UICategory.request,

          data: {
            customerId: customer._id,
            accountId: oaa.accountId,
            accountCreatedTime: oaa.accountCreatedTime,
          },
        },
        'Primary identity is undefined'
      );

      oaa.status = OnlineAccountStatuses.primaryIdentityNotFound;
      return oaa;
    }

    if (customerAccount.linkingPending) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'getOnlineAccountAccess',
          UICategory: UICategory.request,

          data: {
            customerId: customer._id,
            accountId: customerAccount._id,
          },
        },
        'Linking identity to account is pending'
      );
      oaa.status = OnlineAccountStatuses.linkingPending;
    }

    if (primaryIdentity && primaryIdentity.isLocked) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'IdentityService',
          function: 'getOnlineAccountAccess',
          UICategory: UICategory.request,

          data: {
            customerId: customer._id,
            accountId: customerAccount._id,
            identifier: primaryIdentity.identifier,
            provider: primaryIdentity.provider,
          },
        },
        'Primary login identity is locked'
      );
      oaa.status = OnlineAccountStatuses.locked;
    }

    if (customerAccount.identities.length > 0) {
      for (let i = 0; i < customerAccount.identities.length; i++) {
        const identity = customerAccount.identities[i];
        const fullIdentity: FullIdentity = {
          email: customer.email,
          provider: IdentityProvidersDisplayName[identity.provider],
          identifier: identity.identifier,
          isLocked: identity.isLocked,
          isActive: identity.isActive,
          isPrimary: identity.isPrimary,
        };

        const hasMultipleIdentities = customerAccount.identities.length > 1;
        if (!identity.isPrimary && hasMultipleIdentities && oaa.identityProvider !== OnlineAccountProvider.AMC) {
          UILoggerSingleton.getInstance().info(
            {
              module: 'IdentityService',
              function: 'getOAAForAuthorizedCustomer',
              UICategory: UICategory.request,

              data: {
                customerId: customer._id,
                accountId: customerAccount._id,
                activeIdentities: customerAccount.activeIdentities.length,
              },
            },
            'Customer has multiple active login identities'
          );
          // get auth0 user object
          fullIdentity.email = await this.getEmailFromAuth0ProfileData(primaryIdentity.identifier, identity.identifier);
        }

        oaa.identities.push(fullIdentity);
      }
    }

    return oaa;
  }

  public async getEmailFromAuth0ProfileData(primaryIdentityIdentifier: string, identifier: string): Promise<string> {
    let auth0info;
    try {
      auth0info = await Auth0Client.getAccountInfoById(primaryIdentityIdentifier);
    } catch (error) {
      // dont blow up
      UILoggerSingleton.getInstance().error(
        {
          module: 'IdentityService',
          function: 'getEmailFromAuth0ProfileData',
          UICategory: UICategory.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            data: {
              primaryIdentityIdentifier,
              identifier,
            },
          },
        },
        'Error when Calling Auth0 API to fetch profile data email of linked identities'
      );
    }

    const auth0Identity = auth0info
      ? auth0info.identities.find(
          (auth0identity) => auth0identity.provider + IDENTITY_SEPARATOR + auth0identity.user_id === identifier
        )
      : null;

    return auth0Identity && auth0Identity.profileData ? auth0Identity.profileData.email : '';
  }

  /**
   * Given a customer, return online access account for legacy customer
   * Legacy customers are users that had an account before Auth or Auth0 AMC and have not fully migrated
   * @param customer
   */
  public getOAAForLegacyCustomer(customer: Customer): OnlineAccountAccess {
    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'getOAAForLegacyCustomer',
        UICategory: UICategory.request,

        data: {
          customerId: customer._id,
        },
      },
      'Customer has legacy account'
    );

    return {
      status: OnlineAccountStatuses.legacy,
      accountId: null,
      accountCreatedTime: null,
      accountAuthorizationCreatedTime: null,
      identities: [{ email: customer.email, isPrimary: true }],
      identityProvider: null,
    } as OnlineAccountAccess;
  }

  /**
   * Given a customer, return online access account for not authorized customer
   * Unauthorized customers are ones that have not fully completed the auth process
   * EX: Invite is sent so customer obj exists but user did not login to authorize yet
   * @param customer
   */
  public getOAAUnauthorizedCustomer(customer: Customer): OnlineAccountAccess {
    UILoggerSingleton.getInstance().info(
      {
        module: 'IdentityService',
        function: 'getOAAUnauthorizedCustomer',
        UICategory: UICategory.request,

        data: {
          customerId: customer._id,
        },
      },
      'Identity provider not yet authorized or created'
    );

    return {
      status: OnlineAccountStatuses.unauthorized,
      accountId: null,
      accountCreatedTime: null,
      accountAuthorizationCreatedTime: null,
      identities: [{ email: customer.email, isPrimary: true }],
      identityProvider: null,
    } as OnlineAccountAccess;
  }
}
